import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Send, Trash2, Video } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "../../components/ui/sonner";
import { SkycamPlayer } from "../../components/SkycamPlayer";
import { useSkycamNetwork } from "../../hooks/useSkycamNetwork";
import { useStudioAuth } from "../StudioAuthContext";
import { EmptyState, OPS_INPUT, Panel, StatusChip } from "../ui";

export const SkycamsSection = () => {
  const { request } = useStudioAuth();
  const { stations, loading } = useSkycamNetwork();
  const [cams, setCams] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    const response = await request("/skycams");
    if (response.ok) {
      const data = await response.json();
      setCams(data);
      setSelected((current) => current || data[0] || null);
    }
  }, [request]);

  useEffect(() => { load(); }, [load]);

  const published = useMemo(() => new Set(cams.map((cam) => cam.station)), [cams]);
  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (needle ? stations.filter((station) => `${station.name} ${station.id} ${station.network || ""}`.toLowerCase().includes(needle)) : stations).slice(0, 40);
  }, [stations, query]);

  const addStation = async (station) => {
    const response = await request("/operations/skycams", { method: "POST", body: JSON.stringify({ station: station.id, enabled: true }) });
    if (!response.ok) return toast.error("Skycam could not be added");
    await load();
    toast.success(`${station.name} added to Storm Desk picks`);
  };

  const toggle = async (cam) => {
    const response = await request(`/operations/skycams/${cam.id}`, { method: "PATCH", body: JSON.stringify({ enabled: !cam.enabled }) });
    if (!response.ok) return toast.error("Skycam could not be updated");
    await load();
    toast.success(cam.enabled ? "Hidden from the public site" : "Published to the public site");
  };

  const remove = async (camId) => {
    await request(`/operations/skycams/${camId}`, { method: "DELETE" });
    setSelected((current) => (current?.id === camId ? null : current));
    await load();
  };

  const sendToPreview = async (cam) => {
    const response = await request("/broadcast", {
      method: "POST",
      body: JSON.stringify({ preview_scene: "skycam", skycam_url: cam.video_url, skycam_name: cam.name, lower_third_title: cam.name.toUpperCase(), lower_third_subtitle: cam.location || cam.network || "HazCam skycam" }),
    });
    if (response.ok) toast.success(`${cam.name} staged in Preview`);
    else toast.error("Could not stage the skycam");
  };

  return (
    <div className="grid gap-3 xl:grid-cols-[1fr_360px]">
      <div className="space-y-3">
        <Panel
          title="Skycam Monitor"
          icon={Video}
          note={selected ? selected.name : "No camera selected"}
          actions={selected && (
            <button data-testid="skycam-send-to-preview-button" onClick={() => sendToPreview(selected)} className="mono flex h-7 items-center gap-1.5 bg-[#1a5cff] px-2.5 text-[9px] font-bold uppercase tracking-widest text-white hover:bg-[#2a6cff]">
              <Send className="h-3.5 w-3.5" /> To Preview
            </button>
          )}
          bodyClass="p-3"
        >
          <div className="aspect-video border border-white/10 bg-black">
            {selected ? (
              <SkycamPlayer cam={selected} testId="skycam-studio-player" />
            ) : (
              <div className="grid h-full place-items-center">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-600">Select a HazCam station to monitor it here</div>
              </div>
            )}
          </div>
        </Panel>

        <Panel title="Storm Desk picks" icon={Video} note={`${cams.filter((cam) => cam.enabled).length} published`} bodyClass="p-0">
          {cams.length === 0 ? (
            <EmptyState icon={Video} title="No picks yet" message="Search the HazCam network on the right and add stations to feature them on the public Skycams page." testId="skycams-studio-empty" />
          ) : (
            <div className="divide-y divide-[rgba(120,170,255,0.12)]">
              {cams.map((cam) => (
                <div key={cam.id} data-testid={`studio-skycam-${cam.id}`} className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${selected?.id === cam.id ? "bg-white/[0.03]" : ""}`}>
                  <button onClick={() => setSelected(cam)} data-testid={`studio-skycam-select-${cam.id}`} className="flex min-w-0 items-center gap-3 text-left">
                    <img src={cam.poster} alt="" className="h-9 w-16 shrink-0 object-cover" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold text-white">{cam.name}</div>
                      <div className="mono mt-0.5 truncate text-[9px] uppercase tracking-widest text-slate-600">{cam.location || "HazCam"} · {cam.station}</div>
                    </div>
                  </button>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusChip state={cam.enabled ? "ok" : "idle"} dot={false}>{cam.enabled ? "Public" : "Hidden"}</StatusChip>
                    <Switch data-testid={`skycam-toggle-${cam.id}`} checked={cam.enabled} onCheckedChange={() => toggle(cam)} />
                    <button data-testid={`skycam-delete-${cam.id}`} onClick={() => remove(cam.id)} title="Remove from picks" className="grid h-8 w-8 place-items-center border border-white/10 text-slate-500 hover:border-red-500/30 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="HazCam Network" icon={Search} note={loading ? "Loading…" : `${stations.length} stations`} className="h-fit" bodyClass="p-3">
        <Input data-testid="skycam-network-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search city, state or station id…" className={OPS_INPUT} />
        <div className="mt-2 max-h-[560px] divide-y divide-white/5 overflow-y-auto border border-white/10" data-testid="skycam-network-results">
          {!loading && results.length === 0 && <div className="mono p-4 text-center text-[10px] uppercase tracking-widest text-slate-500">No stations match</div>}
          {results.map((station) => (
            <div key={station.id} data-testid={`network-station-${station.id}`} className="flex items-center gap-2 px-2 py-2">
              <button type="button" onClick={() => setSelected({ ...station, name: station.name, location: station.network })} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid={`network-station-monitor-${station.id}`}>
                <img src={station.poster} alt="" loading="lazy" className="h-8 w-14 shrink-0 object-cover" />
                <div className="min-w-0">
                  <div className="truncate text-xs font-semibold text-slate-100">{station.name}</div>
                  <div className="mono truncate text-[9px] uppercase tracking-widest text-slate-500">{station.online ? "Live" : "Offline"}{station.network ? ` · ${station.network}` : ""}</div>
                </div>
              </button>
              {published.has(station.id) ? (
                <StatusChip state="ok" dot={false}>Pick</StatusChip>
              ) : (
                <button type="button" data-testid={`network-station-add-${station.id}`} onClick={() => addStation(station)} title="Add to Storm Desk picks" className="grid h-7 w-7 shrink-0 place-items-center border border-white/15 text-slate-300 hover:bg-[#1a5cff] hover:text-white"><Plus className="h-3.5 w-3.5" /></button>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};

export default SkycamsSection;
